import _ from "lodash";
import React, { Fragment, useRef } from "react";
import classes from "./IconPicker.scss";
import DropzoneComponent from "UIComponents/DropzoneComponent";
import { useI18n } from "Hooks";
import { Popover, ArrowContainer } from "@toddle-edu/react-tiny-popover";
import { colors } from "Constants";
import { Button } from "@toddle-edu/ds-web";
import { DeviceDesktopOutlined } from "@toddle-edu/ds-icons";
import classNames from "classnames";
import { dropZoneStyle } from "./IconPickerStyles";
import useIconPickerAccessibility from "./IconPickerAccessibilityHook";

const IconPickerPopover = props => {
  const {
    position,
    childRect,
    popoverRect,
    toggleIconPicker,
    onSelectImage,
    iconList,
    onUpdateIcon,
    showUploadOption,
    selectedIconName,
  } = props;

  const { t } = useI18n(["common"]);
  const uploadButtonRef = useRef(null);
  const iconRefs = useRef({});
  const indexToIconNameMapRef = useRef({});

  const popoverHeaderTextClasses = classNames("heading-6");

  const { handleKeyDown } = useIconPickerAccessibility({
    iconRefs,
    indexToIconNameMapRef,
    selectedIconName,
    uploadButtonRef,
  });

  return (
    <ArrowContainer
      position={position}
      childRect={childRect}
      popoverRect={popoverRect}
      arrowColor={colors.white}
      arrowSize={10}
    >
      <div className={classes.mainContainer}>
        {showUploadOption ? (
          <Fragment>
            <div
              className={classes.header}
              onKeyDown={e =>
                handleKeyDown({
                  e,
                  isUploadButtonEvent: true,
                  toggleIconPicker,
                })
              }
            >
              <div className={popoverHeaderTextClasses}>
                {t("common:upload_an_image")}
              </div>

              <DropzoneComponent
                multiple={false}
                accept="image/jpeg,image/png,image/jpg,image/heic,image/heif"
                dropZoneStyle={dropZoneStyle}
                shouldUpload={false}
                renderComponent={
                  <Button
                    icon={<DeviceDesktopOutlined size="xxx-small" />}
                    size="small"
                    variant="outlined-subtle"
                    ref={uploadButtonRef}
                  >
                    {t("common:upload")}
                  </Button>
                }
                onDropContent={toggleIconPicker}
                onContentUploaded={attachments => {
                  if (_.size(attachments) > 0) {
                    onSelectImage(_.first(attachments));
                  }
                }}
              />
            </div>

            <div className={classes.separator} />
          </Fragment>
        ) : null}

        <div className={popoverHeaderTextClasses}>
          {showUploadOption
            ? t("common:or_select_an_icon")
            : t("common:select_an_icon")}
        </div>
        <div className={classes.iconListContainer}>
          {_.map(iconList, (item, index) => {
            return (
              <button
                key={item.iconName}
                ref={ref => {
                  iconRefs.current[index] = ref;
                  indexToIconNameMapRef.current[index] = item.iconName;
                }}
                onKeyDown={e =>
                  handleKeyDown({
                    e,
                    index,
                    iconList,
                    toggleIconPicker,
                  })
                }
                onClick={event => {
                  toggleIconPicker(event);
                  onUpdateIcon({
                    icon: {
                      name: item.displayName,
                      type: "IMAGE",
                      metadata: {
                        iconName: item.iconName,
                      },
                    },
                  });
                }}
                className={classes.iconContainer}
              >
                {item.component}
              </button>
            );
          })}
        </div>
      </div>
    </ArrowContainer>
  );
};

const IconPicker = props => {
  const {
    toggleIconPicker,
    iconList,
    onUpdateIcon,
    onSelectImage,
    containerParentRef,
    childrenRef,
    showUploadOption = true,
    selectedIconName,
  } = props;

  return (
    <Popover
      isOpen={true}
      positions={["bottom", "left"]}
      padding={10}
      onClickOutside={toggleIconPicker}
      containerClassName={classes.popoverContainer}
      containerParent={containerParentRef.current}
      childrenRef={childrenRef}
      isChildrenRefPassed
      content={({ position, childRect, popoverRect }) => (
        <IconPickerPopover
          selectedIconName={selectedIconName}
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          iconList={iconList}
          onUpdateIcon={onUpdateIcon}
          onSelectImage={onSelectImage}
          toggleIconPicker={toggleIconPicker}
          showUploadOption={showUploadOption}
        />
      )}
    ></Popover>
  );
};

export default IconPicker;
